<template>
  <div
    ref="menuRef"
    class="relative"
  >
    <!-- Hamburger Button for Mobile & Tablet -->
    <button
      id="mobile-header-hamburger-button"
      type="button"
      class="relative bg-white rounded-full w-9 h-9 md:w-10 md:h-10 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer border border-black/10 shadow-[0_2px_4px_rgba(0,0,0,0.04)] focus:outline-none"
      aria-label="Navigation & Notifications Menu"
      @click="toggleMenu"
    >
      <!-- Hamburger 3-line icon -->
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#043f42"
        stroke-width="2.2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="shrink-0"
      >
        <line
          x1="3"
          y1="6"
          x2="21"
          y2="6"
        />
        <line
          x1="3"
          y1="12"
          x2="21"
          y2="12"
        />
        <line
          x1="3"
          y1="18"
          x2="21"
          y2="18"
        />
      </svg>

      <!-- Unread Notification Indicator on top of hamburger icon -->
      <transition
        enter-active-class="transition transform duration-200 ease-out"
        enter-from-class="scale-0 opacity-0"
        enter-to-class="scale-100 opacity-100"
        leave-active-class="transition transform duration-150 ease-in"
        leave-from-class="scale-100 opacity-100"
        leave-to-class="scale-0 opacity-0"
      >
        <span
          v-if="unreadCount > 0"
          class="absolute -top-1 -right-1 flex h-4 min-w-[16px] px-1 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm ring-2 ring-white"
        >
          {{ displayUnreadCount }}
        </span>
      </transition>
    </button>

    <!-- Context Menu Dropdown -->
    <div
      v-if="isOpen"
      class="absolute right-0 top-full mt-2 w-64 max-w-[calc(100vw-32px)] bg-white rounded-[16px] shadow-2xl border border-gray-100 py-2 z-50 overflow-hidden"
    >
      <!-- User Profile Header -->
      <div class="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
        <div class="w-9 h-9 rounded-full bg-[#01d680] flex items-center justify-center overflow-hidden shrink-0">
          <img
            v-if="user.avatarUrl"
            :src="user.avatarUrl"
            alt="User avatar"
            class="w-full h-full object-cover"
          >
          <span
            v-else
            class="font-['Raleway',sans-serif] text-[13px] font-bold text-white"
          >
            {{ (user.firstName || 'U')[0].toUpperCase() }}
          </span>
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center justify-between gap-1">
            <div class="font-['Raleway',sans-serif] font-bold text-[14px] text-[#043f42] truncate">
              {{ user.firstName }} {{ user.lastName }}
            </div>
            <span class="shrink-0 px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#e6fbf2] text-[#01d680] uppercase tracking-wider">
              {{ user.role || 'Admin' }}
            </span>
          </div>
          <div class="font-['Raleway',sans-serif] text-[12px] text-gray-500 truncate mt-0.5">
            {{ user.email }}
          </div>
        </div>
      </div>

      <!-- Notifications Menu Item (with reduced-size 14px bell icon) -->
      <a
        :href="user.notificationsPath || '/notifications'"
        class="flex items-center justify-between px-4 py-2.5 font-['Raleway',sans-serif] text-sm text-[#043f42] hover:bg-gray-50 transition-colors"
        @click="closeMenu"
      >
        <div class="flex items-center gap-2.5">
          <svg
            width="14"
            height="16"
            viewBox="0 0 17 20"
            fill="none"
            class="shrink-0 text-[#043f42]"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.87558 7.07667C1.87558 3.66281 4.64922 0.889168 8.06308 0.889168C11.4678 0.889168 14.2506 3.67197 14.2506 7.07667V9.72583C14.2506 9.92279 14.2952 10.2111 14.3796 10.5191C14.4631 10.8234 14.5709 11.0939 14.669 11.2656L15.7201 13.0113C16.5502 14.3948 15.8866 16.197 14.3574 16.7032C10.2655 18.0701 5.85044 18.07 1.75855 16.7029L1.756 16.702C0.983793 16.4407 0.404683 15.9003 0.145753 15.201C-0.113477 14.5009 -0.0252919 13.7129 0.397326 13.0106L1.45005 11.2621C1.45028 11.2617 1.45052 11.2613 1.45075 11.261C1.55113 11.0922 1.66108 10.8227 1.74594 10.517C1.83105 10.2104 1.87558 9.92258 1.87558 9.72583V7.07667ZM8.06308 2.26417C5.40861 2.26417 3.25058 4.4222 3.25058 7.07667V9.72583C3.25058 10.0883 3.17594 10.5062 3.07084 10.8848C2.96568 11.2636 2.8147 11.6582 2.6311 11.9662L2.62956 11.9688L1.5755 13.7195C1.57546 13.7195 1.57553 13.7194 1.5755 13.7195C1.34738 14.0987 1.33465 14.452 1.43519 14.7236C1.53596 14.9957 1.77689 15.2571 2.1956 15.3992C6.00466 16.6715 10.1137 16.6714 13.9227 15.3988L13.9248 15.3981C14.613 15.1706 14.9206 14.352 14.5413 13.7192C14.5412 13.719 14.5414 13.7194 14.5413 13.7192L13.4808 11.9578C13.3047 11.6526 13.1572 11.2607 13.0536 10.8828C12.9502 10.5056 12.8756 10.088 12.8756 9.72583V7.07667C12.8756 4.43136 10.7084 2.26417 8.06308 2.26417Z"
              fill="#043f42"
            />
          </svg>
          <span>Notifications</span>
        </div>
        <span
          v-if="unreadCount > 0"
          class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-600"
        >
          {{ unreadCount }} new
        </span>
      </a>

      <!-- Profile Link -->
      <a
        :href="user.profilePath || '/settings/profile'"
        class="flex items-center gap-2.5 px-4 py-2.5 font-['Raleway',sans-serif] text-sm text-[#043f42] hover:bg-gray-50 transition-colors"
        @click="closeMenu"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-[#043f42]"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle
            cx="12"
            cy="7"
            r="4"
          />
        </svg>
        <span>Profile</span>
      </a>

      <!-- Restart Tour Button -->
      <button
        type="button"
        class="w-full text-left flex items-center gap-2.5 px-4 py-2.5 font-['Raleway',sans-serif] text-sm text-[#043f42] hover:bg-gray-50 transition-colors cursor-pointer"
        @click="restartTour"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-[#01d680]"
        >
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
        <span>Restart Tour</span>
      </button>

      <!-- Divider -->
      <div class="mx-4 my-1 border-t border-gray-100" />

      <!-- Sign Out Button -->
      <form
        :action="user.signOutPath || '/users/sign_out'"
        method="post"
        class="w-full"
      >
        <input
          type="hidden"
          name="_method"
          value="delete"
        >
        <input
          type="hidden"
          name="authenticity_token"
          :value="user.authenticityToken"
        >
        <button
          type="submit"
          class="w-full text-left px-4 py-2.5 font-['Raleway',sans-serif] text-sm text-red-600 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2.5"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-red-600"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line
              x1="21"
              y1="12"
              x2="9"
              y2="12"
            />
          </svg>
          <span>Sign Out</span>
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNotifications } from '../../composables/useNotifications'

const props = defineProps({
  userJson: {
    type: String,
    default: '{}'
  }
})

const { unreadCount, fetchRecent, startPolling, stopPolling } = useNotifications()

const isOpen = ref(false)
const menuRef = ref(null)

const user = computed(() => {
  try {
    return JSON.parse(props.userJson || '{}')
  } catch (e) {
    return {}
  }
})

const displayUnreadCount = computed(() => {
  return unreadCount.value > 99 ? '99+' : unreadCount.value
})

const toggleMenu = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    fetchRecent()
  }
}

const closeMenu = () => {
  isOpen.value = false
}

const restartTour = () => {
  closeMenu()
  const btn = document.getElementById('header-restart-tour-button')
  if (btn) {
    btn.click()
  } else {
    window.dispatchEvent(new CustomEvent('tour:restart'))
  }
}

const handleClickOutside = (event) => {
  if (isOpen.value && menuRef.value && !menuRef.value.contains(event.target)) {
    closeMenu()
  }
}

onMounted(() => {
  startPolling(30000)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  stopPolling()
  document.removeEventListener('click', handleClickOutside)
})
</script>
